import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Icon,
  ListGroup,
  ListRow,
  SegmentedControl,
  Skeleton,
  Switch,
  TextField,
} from '@uplink/ui';
import { useEffect, useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import { PointToServer } from '../domain-point.tsx';
import { MainActionBar } from '../native-controls.tsx';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { haptic } from '../telegram.ts';

type RecordType = 'A' | 'AAAA' | 'CNAME' | 'MX' | 'TXT';
interface DnsRecord {
  host: string;
  type: RecordType;
  value: string;
  priority?: number;
}

const TYPES: { value: RecordType; label: string }[] = [
  { value: 'A', label: 'A' },
  { value: 'AAAA', label: 'AAAA' },
  { value: 'CNAME', label: 'CNAME' },
  { value: 'MX', label: 'MX' },
  { value: 'TXT', label: 'TXT' },
];

const DAY = 86_400_000;

export function DomainScreen({ nav, id }: { nav: Nav; id: string }) {
  const trpc = useTRPC();
  const { t, money, date } = usePrefs();
  const queryClient = useQueryClient();
  const domain = useQuery(trpc.domains.get.queryOptions({ id }));
  const balance = useQuery(trpc.balance.summary.queryOptions());
  const d = domain.data;
  const dns = useQuery({ ...trpc.domains.dns.queryOptions({ id }), enabled: !!d?.manageable });

  const refresh = () => {
    queryClient.invalidateQueries(trpc.domains.pathFilter());
    queryClient.invalidateQueries(trpc.balance.pathFilter());
  };
  const renew = useMutation(
    trpc.domains.renew.mutationOptions({
      onSuccess: (o) => {
        if (o.status === 'active') haptic.success();
        refresh();
      },
      onError: () => haptic.error(),
    }),
  );
  const autoRenew = useMutation(
    trpc.domains.setAutoRenew.mutationOptions({ onSuccess: refresh, onError: () => haptic.error() }),
  );

  if (domain.isPending) {
    return (
      <main className="ul-screen">
        <Skeleton height={96} />
        <Skeleton height={120} />
      </main>
    );
  }
  if (!d) {
    return (
      <main className="ul-screen">
        <div className="ul-error" role="alert">
          {errorText(domain.error)}
        </div>
      </main>
    );
  }

  const dot = d.name.indexOf('.');
  const daysLeft = d.expiresAt ? Math.ceil((Date.parse(d.expiresAt) - Date.now()) / DAY) : null;
  const shortBy =
    d.renewCents !== null && balance.data ? Math.max(0, d.renewCents - balance.data.balanceCents) : 0;
  const renewed = renew.data?.status === 'active';

  return (
    <main className="ul-screen ul-screen--with-action">
      <section className="ul-summary" aria-label={t.checkout.domain}>
        <span className="ul-summary__name">
          {d.name.slice(0, dot)}
          <b>{d.name.slice(dot)}</b>
        </span>
        <StatusBadge status={d.status} daysLeft={daysLeft} />
      </section>

      <ListGroup header={t.domain.renewal} footer={d.autoRenew ? t.domain.autoFooter : t.domain.manualFooter}>
        <ListRow icon="clock" title={t.domain.validUntil} value={d.expiresAt ? date(d.expiresAt) : '—'} />
        <ListRow
          icon="wallet"
          title={t.common.autoRenew}
          subtitle={d.renewCents !== null ? `${money(d.renewCents)} ${t.common.perYear}` : undefined}
          trailing={
            <Switch
              label={t.common.autoRenew}
              checked={d.autoRenew}
              disabled={autoRenew.isPending}
              onChange={(on) => {
                haptic.select();
                autoRenew.mutate({ id, autoRenew: on });
              }}
            />
          }
        />
      </ListGroup>
      {renew.isError ? (
        <div className="ul-error" role="alert">
          {errorText(renew.error)}
        </div>
      ) : null}
      {renewed ? (
        <div className="ul-success__text" role="status">
          {t.domain.renewed}
        </div>
      ) : null}

      {d.manageable ? (
        <>
          <PointToServer id={id} records={dns.data?.records ?? []} onDone={refresh} />
          <DnsSection id={id} dns={dns} onSaved={refresh} />
          <TransferSection id={id} />
        </>
      ) : null}

      {d.manageable && d.renewCents !== null ? (
        <MainActionBar
          action={
            shortBy > 0
              ? {
                  text: t.common.topUpBy(money(Math.max(shortBy, 500))),
                  onClick: () =>
                    nav.push({
                      name: 'topup',
                      amountCents: Math.max(shortBy, 500),
                      after: { kind: 'domainRenew', id, label: d.name },
                    }),
                }
              : {
                  text: t.domain.renewFor(money(d.renewCents)),
                  onClick: () => renew.mutate({ id }),
                  loading: renew.isPending,
                  disabled: !balance.data,
                }
          }
        />
      ) : null}
    </main>
  );
}

function StatusBadge({ status, daysLeft }: { status: string; daysLeft: number | null }) {
  const { t } = usePrefs();
  if (status === 'pending') return <Badge tone="accent">{t.domain.statusPending}</Badge>;
  if (status === 'expired' || (daysLeft !== null && daysLeft <= 0))
    return <Badge tone="crit">{t.domain.statusExpired}</Badge>;
  if (daysLeft !== null && daysLeft <= 30)
    return <Badge tone="warn">{t.domain.statusExpiresIn(daysLeft)}</Badge>;
  return <Badge tone="ok">{t.domain.statusActive}</Badge>;
}

function DnsSection({
  id,
  dns,
  onSaved,
}: {
  id: string;
  dns: {
    data?: { mode: 'registrar' | 'nameservers'; nameservers: string[]; records: DnsRecord[] };
    isPending: boolean;
    error: unknown;
    isError: boolean;
  };
  onSaved: () => void;
}) {
  const trpc = useTRPC();
  const { t } = usePrefs();
  const [mode, setMode] = useState<'registrar' | 'nameservers'>('registrar');
  const [records, setRecords] = useState<DnsRecord[]>([]);
  const [nameservers, setNameservers] = useState<string[]>(['', '']);
  const [draft, setDraft] = useState<DnsRecord>({ host: '@', type: 'A', value: '' });
  const [dirty, setDirty] = useState(false);

  // Start from what the registrar has, until the user edits something
  useEffect(() => {
    if (!dns.data || dirty) return;
    setMode(dns.data.mode);
    setRecords(dns.data.records);
    setNameservers(dns.data.nameservers.length >= 2 ? dns.data.nameservers : ['', '']);
  }, [dns.data, dirty]);

  const saveRecords = useMutation(
    trpc.domains.setRecords.mutationOptions({
      onSuccess: () => {
        haptic.success();
        setDirty(false);
        onSaved();
      },
      onError: () => haptic.error(),
    }),
  );
  const saveNs = useMutation(
    trpc.domains.setNameservers.mutationOptions({
      onSuccess: () => {
        haptic.success();
        setDirty(false);
        onSaved();
      },
      onError: () => haptic.error(),
    }),
  );

  if (dns.isPending) return <Skeleton height={160} />;
  if (dns.isError) {
    return (
      <div className="ul-error" role="alert">
        {t.domain.dnsFailed(errorText(dns.error))}
      </div>
    );
  }

  const switching = dns.data && dns.data.mode !== mode;
  const addDraft = () => {
    if (!draft.value.trim()) return;
    haptic.tap();
    setRecords((r) => [...r, { ...draft, host: draft.host.trim() || '@' }]);
    setDraft({ host: '@', type: draft.type, value: '' });
    setDirty(true);
  };

  return (
    <section className="ul-stack" aria-label="DNS">
      <div className="ul-group__header">DNS</div>
      <SegmentedControl
        label={t.domain.dnsWhere}
        options={[
          { value: 'registrar', label: t.domain.dnsUplink },
          { value: 'nameservers', label: t.domain.dnsOwnNs },
        ]}
        value={mode}
        onChange={(m) => {
          haptic.select();
          setMode(m);
          setDirty(true);
        }}
      />

      {mode === 'registrar' ? (
        <>
          {records.length > 0 ? (
            <div className="ul-group">
              {records.map((r, i) => (
                <ListRow
                  key={i}
                  title={r.host === '@' ? t.domain.apex : r.host}
                  mono
                  subtitle={`${r.type}${r.type === 'MX' ? ` ${r.priority ?? 10}` : ''} · ${r.value}`}
                  trailing={
                    <button
                      type="button"
                      className="ul-icon-btn ul-icon-btn--crit"
                      aria-label={t.domain.deleteRecord(r.host)}
                      onClick={() => {
                        haptic.tap();
                        setRecords((all) => all.filter((_, j) => j !== i));
                        setDirty(true);
                      }}
                    >
                      <Icon name="trash" size={20} />
                    </button>
                  }
                />
              ))}
            </div>
          ) : (
            <div className="ul-group__footer">{t.domain.noRecords}</div>
          )}

          <div className="ul-form">
            <SegmentedControl
              label={t.domain.recordType}
              options={TYPES}
              value={draft.type}
              onChange={(type) => setDraft((r) => ({ ...r, type }))}
            />
            <div className={draft.type === 'MX' ? 'ul-form__row' : 'ul-stack'}>
              <TextField
                id="dns-host"
                label={t.domain.host}
                value={draft.host}
                placeholder="@"
                onChange={(host) => setDraft((r) => ({ ...r, host }))}
                hint={t.domain.hostHint}
              />
              {draft.type === 'MX' ? (
                <TextField
                  id="dns-prio"
                  label={t.domain.priority}
                  inputMode="numeric"
                  value={String(draft.priority ?? 10)}
                  onChange={(v) => setDraft((r) => ({ ...r, priority: Number(v.replace(/\D/g, '')) || 0 }))}
                />
              ) : null}
            </div>
            <TextField
              id="dns-value"
              label={t.domain.value}
              value={draft.value}
              onChange={(value) => setDraft((r) => ({ ...r, value }))}
              hint={t.domain.valueHint[draft.type]}
            />
            <Button variant="secondary" icon="plus" block onClick={addDraft} disabled={!draft.value.trim()}>
              {t.domain.addRecord}
            </Button>
          </div>

          {saveRecords.isError ? (
            <div className="ul-error" role="alert">
              {errorText(saveRecords.error)}
            </div>
          ) : null}
          {dirty || switching ? (
            <>
              {switching ? <div className="ul-group__footer">{t.domain.switchToUplink}</div> : null}
              <Button
                block
                loading={saveRecords.isPending}
                disabled={records.length === 0}
                onClick={() => saveRecords.mutate({ id, records })}
              >
                {t.domain.saveRecords}
              </Button>
            </>
          ) : null}
        </>
      ) : (
        <>
          <div className="ul-form">
            {nameservers.map((ns, i) => (
              <TextField
                key={i}
                id={`ns-${i}`}
                label={`NS ${i + 1}`}
                value={ns}
                placeholder={`ns${i + 1}.example.com`}
                onChange={(v) => {
                  setNameservers((all) => all.map((x, j) => (j === i ? v : x)));
                  setDirty(true);
                }}
              />
            ))}
            {nameservers.length < 4 ? (
              <button type="button" className="ul-link" onClick={() => setNameservers((all) => [...all, ''])}>
                <Icon name="plus" size={16} />
                {t.domain.moreNs}
              </button>
            ) : null}
          </div>
          <div className="ul-group__footer">{t.domain.nsFooter}</div>
          {saveNs.isError ? (
            <div className="ul-error" role="alert">
              {errorText(saveNs.error)}
            </div>
          ) : null}
          {dirty || switching ? (
            <Button
              block
              loading={saveNs.isPending}
              disabled={nameservers.filter((s) => s.trim()).length < 2}
              onClick={() => saveNs.mutate({ id, nameservers: nameservers.filter((s) => s.trim()) })}
            >
              {t.domain.saveNs}
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}

function TransferSection({ id }: { id: string }) {
  const trpc = useTRPC();
  const { t } = usePrefs();
  const code = useMutation(trpc.domains.authCode.mutationOptions({ onError: () => haptic.error() }));
  const [copied, setCopied] = useState(false);

  return (
    <ListGroup header={t.domain.transfer} footer={t.domain.transferFooter}>
      {code.data ? (
        <ListRow
          title={code.data.authCode}
          mono
          subtitle={t.domain.authCode}
          trailing={
            <button
              type="button"
              className={`ul-copy${copied ? ' is-done' : ''}`}
              onClick={() => {
                navigator.clipboard?.writeText(code.data.authCode).then(() => {
                  haptic.success();
                  setCopied(true);
                });
              }}
            >
              <Icon name={copied ? 'check' : 'copy'} size={16} />
              {copied ? t.common.copied : t.common.copy}
            </button>
          }
        />
      ) : (
        <ListRow
          icon="key"
          title={t.domain.authCode}
          subtitle={code.isError ? errorText(code.error) : undefined}
          tone={code.isError ? 'warn' : undefined}
          trailing={
            <Button variant="ghost" loading={code.isPending} onClick={() => code.mutate({ id })}>
              {t.common.show}
            </Button>
          }
        />
      )}
    </ListGroup>
  );
}
